import type { IceCandidate, ServerMessage, SignalPayload } from '../../shared/signaling.js';
import { PeerConnectionError, PeerConnectionFailedError } from './errors.js';
import type { SignalingClient, Unsubscribe } from './signaling-client.js';

const toIceCandidate = (candidate: RTCIceCandidate): IceCandidate => ({
  candidate: candidate.candidate,
  sdpMid: candidate.sdpMid,
  sdpMLineIndex: candidate.sdpMLineIndex,
});

const toDescriptionPayload = (description: RTCSessionDescriptionInit): SignalPayload => {
  if ((description.type !== 'offer' && description.type !== 'answer') || description.sdp === undefined) {
    throw new PeerConnectionError(`Local description is incomplete: ${JSON.stringify(description)}`);
  }
  return { kind: 'description', description: { type: description.type, sdp: description.sdp } };
};

/**
 * Connects an RTCPeerConnection to the signaling channel.
 * Both roles use the same logic: offers are answered automatically, answers are applied,
 * and ICE candidates are queued until the remote description exists.
 */
export const wireSignaling = (pc: RTCPeerConnection, signaling: SignalingClient): Unsubscribe => {
  const pendingCandidates: IceCandidate[] = [];
  let remoteDescriptionSet = false;

  pc.addEventListener('icecandidate', (event) => {
    if (event.candidate !== null && event.candidate.candidate !== '') {
      signaling.send({ type: 'signal', payload: { kind: 'candidate', candidate: toIceCandidate(event.candidate) } });
    }
  });

  const flushCandidates = async (): Promise<void> => {
    while (pendingCandidates.length > 0) {
      const candidate = pendingCandidates.shift();
      if (candidate !== undefined) {
        await pc.addIceCandidate(candidate);
      }
    }
  };

  const applyDescription = async (payload: Extract<SignalPayload, { kind: 'description' }>): Promise<void> => {
    await pc.setRemoteDescription(payload.description);
    remoteDescriptionSet = true;
    await flushCandidates();
    if (payload.description.type === 'offer') {
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      signaling.send({ type: 'signal', payload: toDescriptionPayload(answer) });
    }
  };

  const applyCandidate = async (payload: Extract<SignalPayload, { kind: 'candidate' }>): Promise<void> => {
    if (!remoteDescriptionSet) {
      pendingCandidates.push(payload.candidate);
      return;
    }
    await pc.addIceCandidate(payload.candidate);
  };

  const onMessage = (message: ServerMessage): void => {
    if (message.type !== 'signal') {
      return;
    }
    const task = message.payload.kind === 'description' ? applyDescription(message.payload) : applyCandidate(message.payload);
    task.catch((error: unknown) => {
      pc.dispatchEvent(new CustomEvent('signalingfailure', { detail: error }));
    });
  };

  return signaling.onMessage(onMessage);
};

export const startOffer = async (pc: RTCPeerConnection, signaling: SignalingClient): Promise<void> => {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  signaling.send({ type: 'signal', payload: toDescriptionPayload(offer) });
};

export const waitForChannelOpen = (channel: RTCDataChannel): Promise<void> =>
  new Promise((resolve, reject) => {
    if (channel.readyState === 'open') {
      resolve();
      return;
    }
    channel.addEventListener('open', () => resolve(), { once: true });
    channel.addEventListener('error', () => reject(new PeerConnectionError('Data channel failed to open')), { once: true });
    channel.addEventListener('close', () => reject(new PeerConnectionError('Data channel closed before opening')), { once: true });
  });

export const waitForDataChannel = (pc: RTCPeerConnection, label: string): Promise<RTCDataChannel> =>
  new Promise((resolve, reject) => {
    pc.addEventListener('datachannel', (event) => {
      if (event.channel.label === label) {
        resolve(event.channel);
      }
    });
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'failed') {
        reject(new PeerConnectionError('Peer connection failed before a data channel was opened'));
      }
    });
  });

/** Rejects when the peer connection transitions to failed, so callers can race it against the transfer. */
export const watchConnectionFailure = (pc: RTCPeerConnection): Promise<never> =>
  new Promise((_resolve, reject) => {
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'failed') {
        reject(new PeerConnectionFailedError('Peer connection failed. Check network or TURN configuration.'));
      }
    });
    pc.addEventListener('signalingfailure', (event) => {
      reject(new PeerConnectionError(`Signaling negotiation failed: ${String((event as CustomEvent).detail)}`));
    });
  });
